
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * LastIdsGetResult struct
 */
class LastIdsGetResult {
  /**
   * Initialize struct
   * @param {!number=} orderLastId
   * @param {!number=} tradeEventTransactionLastId
   * @param {!number=} personLastId
   * @constructor
   */
  constructor (orderLastId = new UInt64(0, 0), tradeEventTransactionLastId = new UInt64(0, 0), personLastId = new UInt64(0, 0)) {
    this.OrderLastId = orderLastId
    this.TradeEventTransactionLastId = tradeEventTransactionLastId
    this.PersonLastId = personLastId
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LastIdsGetResult}
   * @param {!LastIdsGetResult} other Other struct
   * @returns {!LastIdsGetResult} This struct
   */
  copy (other) {
    if (other.OrderLastId != null) {
      this.OrderLastId = UInt64.fromNumber(other.OrderLastId)
    } else {
      this.OrderLastId = null
    }
    if (other.TradeEventTransactionLastId != null) {
      this.TradeEventTransactionLastId = UInt64.fromNumber(other.TradeEventTransactionLastId)
    } else {
      this.TradeEventTransactionLastId = null
    }
    if (other.PersonLastId != null) {
      this.PersonLastId = UInt64.fromNumber(other.PersonLastId)
    } else {
      this.PersonLastId = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LastIdsGetResult}
   * @returns {!LastIdsGetResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LastIdsGetResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LastIdsGetResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LastIdsGetResult}
   * @param {!LastIdsGetResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LastIdsGetResult)) {
      throw new TypeError('Instance of LastIdsGetResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LastIdsGetResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      OrderLastId: ((this.OrderLastId != null) ? this.OrderLastId.toNumber() : null),
      TradeEventTransactionLastId: ((this.TradeEventTransactionLastId != null) ? this.TradeEventTransactionLastId.toNumber() : null),
      PersonLastId: ((this.PersonLastId != null) ? this.PersonLastId.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LastIdsGetResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LastIdsGetResult} other Object value
   * @returns {!LastIdsGetResult} Created struct
   */
  static fromObject (other) {
    return new LastIdsGetResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LastIdsGetResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LastIdsGetResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LastIdsGetResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10045
  }
}

export { LastIdsGetResult };

/**
 * Fast Binary Encoding LastIdsGetResult field model
 */
class FieldModelLastIdsGetResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.OrderLastId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.TradeEventTransactionLastId = new fbe.FieldModelUInt64(buffer, this.OrderLastId.fbeOffset + this.OrderLastId.fbeSize)
    this.PersonLastId = new fbe.FieldModelUInt64(buffer, this.TradeEventTransactionLastId.fbeOffset + this.TradeEventTransactionLastId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLastIdsGetResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLastIdsGetResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.OrderLastId.fbeSize + this.TradeEventTransactionLastId.fbeSize + this.PersonLastId.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLastIdsGetResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.OrderLastId.fbeExtra + this.TradeEventTransactionLastId.fbeExtra + this.PersonLastId.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLastIdsGetResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLastIdsGetResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLastIdsGetResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10045
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLastIdsGetResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLastIdsGetResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.OrderLastId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.OrderLastId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderLastId.fbeSize

    if ((fbeCurrentSize + this.TradeEventTransactionLastId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.TradeEventTransactionLastId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradeEventTransactionLastId.fbeSize

    if ((fbeCurrentSize + this.PersonLastId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PersonLastId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PersonLastId.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLastIdsGetResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLastIdsGetResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLastIdsGetResult}
   * @param {!LastIdsGetResult} fbeValue Default value, defaults is new LastIdsGetResult()
   * @returns {!LastIdsGetResult} LastIdsGetResult value
   */
  get (fbeValue = new LastIdsGetResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLastIdsGetResult}
   * @param {!LastIdsGetResult} fbeValue LastIdsGetResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.OrderLastId.fbeSize) <= fbeStructSize) {
      fbeValue.OrderLastId = this.OrderLastId.get()
    } else {
      fbeValue.OrderLastId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.OrderLastId.fbeSize

    if ((fbeCurrentSize + this.TradeEventTransactionLastId.fbeSize) <= fbeStructSize) {
      fbeValue.TradeEventTransactionLastId = this.TradeEventTransactionLastId.get()
    } else {
      fbeValue.TradeEventTransactionLastId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.TradeEventTransactionLastId.fbeSize

    if ((fbeCurrentSize + this.PersonLastId.fbeSize) <= fbeStructSize) {
      fbeValue.PersonLastId = this.PersonLastId.get()
    } else {
      fbeValue.PersonLastId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PersonLastId.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLastIdsGetResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLastIdsGetResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLastIdsGetResult}
   * @param {!LastIdsGetResult} fbeValue LastIdsGetResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLastIdsGetResult}
   * @param {LastIdsGetResult} fbeValue LastIdsGetResult value
   */
  setFields (fbeValue) {
    this.OrderLastId.set(fbeValue.OrderLastId)
    this.TradeEventTransactionLastId.set(fbeValue.TradeEventTransactionLastId)
    this.PersonLastId.set(fbeValue.PersonLastId)
  }
}

export { FieldModelLastIdsGetResult };

/**
 * Fast Binary Encoding LastIdsGetResult model
 */
class LastIdsGetResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLastIdsGetResult(this.buffer, 4)
  }

  /**
   * Get the LastIdsGetResult model
   * @this {!LastIdsGetResultModel}
   * @returns {!FieldModelLastIdsGetResult} model LastIdsGetResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LastIdsGetResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LastIdsGetResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LastIdsGetResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LastIdsGetResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLastIdsGetResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LastIdsGetResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LastIdsGetResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LastIdsGetResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LastIdsGetResultModel}
   * @param {!LastIdsGetResult} value LastIdsGetResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LastIdsGetResultModel}
   * @param {!LastIdsGetResult} value LastIdsGetResult value, defaults is new LastIdsGetResult()
   * @return {!object} Deserialized LastIdsGetResult value and its size
   */
  deserialize (value = new LastIdsGetResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LastIdsGetResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LastIdsGetResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LastIdsGetResultModel}
   * @param {!number} prev Previous LastIdsGetResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LastIdsGetResultModel };
